import { connect, Field, getIn } from 'formik';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { css, useTheme } from 'styled-components';

import { AdditionalInformation } from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';
import { Label } from '@hivebrite/design-system/Label';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';
import { getErrorId, getTooltipId } from '@hivebrite/utils';

import InputLocationLegacy from 'hb-react/shared/components/DesignSystem/Inputs/InputLocation/InputLocationLegacy';

import { validateLocation } from './validate';

/**
 * Initial values for location input looks like
 * attributeName: {
 *   city: 'Paris',
 *   country: 'France',
 *   country_code: 'FR',
 *   lat: 48.856614,
 *   lng: 2.3522219,
 *   location: 'Paris, France',
 *   location_level: 'locality',
 * },
 */

class LocationLegacy extends Component {
  handleChange = (value = {}) => {
    const { fieldAddressName, formik, name, onBlur, onChange } = this.props;

    formik.setFieldTouched(`${name}[lat]`, true, false);

    formik.setFieldValue(
      `${name}[colloquial_area]`,
      value?.colloquial_area || '',
      false,
    );
    formik.setFieldValue(
      `${name}[neighborhood]`,
      value?.neighborhood || '',
      false,
    );
    formik.setFieldValue(
      `${name}[administrative_area_level_1]`,
      value?.administrative_area_level_1 || '',
      false,
    );
    formik.setFieldValue(
      `${name}[administrative_area_level_2]`,
      value?.administrative_area_level_2 || '',
      false,
    );
    formik.setFieldValue(`${name}[provider]`, value?.provider || '', false);

    formik.setFieldValue(`${name}[city]`, value?.city || '', false);
    formik.setFieldValue(`${name}[country]`, value?.country || '', false);
    formik.setFieldValue(
      `${name}[country_code]`,
      value?.country_code || '',
      false,
    );
    formik.setFieldValue(`${name}[lat]`, value?.lat || '');
    formik.setFieldValue(`${name}[lng]`, value?.lng || '', false);
    formik.setFieldValue(`${name}[bounds]`, value?.bounds || '', false);
    formik.setFieldValue(
      `${name}[${fieldAddressName}]`,
      value?.location || '',
      false,
    );
    formik.setFieldValue(
      `${name}[location_level]`,
      value?.location_level || '',
      false,
    );

    if (onChange) {
      onChange(value);
    }

    if (onBlur) {
      onBlur();
    }
  };

  handleUpdateOnline = (onlineSelected) => {
    const { formik, name, onChange, onBlur, onlineEnabled, onlineName } =
      this.props;

    if (!onlineEnabled) {
      return;
    }

    formik.setFieldValue(`${name}[${onlineName}]`, onlineSelected);

    if (onlineSelected && onChange) {
      onChange(null);
    }

    if (onBlur) {
      onBlur();
    }
  };

  // Use trough ref, do not remove
  // eslint-disable-next-line react/no-unused-class-component-methods
  handleCleanTextInput = () => {
    if (this.inputLocation) {
      this.inputLocation.resetLocationState();
    }
  };

  render() {
    const {
      ariaDescribedBy,
      disabled,
      fieldAddressName,
      formik,
      hideError,
      icon,
      id,
      label,
      labelMultiline,
      name,
      onlineEnabled,
      onlineLabel,
      placeholder,
      theme,
      tooltip,
      validate: { required },
      variant,
      wrapperProps,
    } = this.props;
    const htmlId = id || name;
    const errorId = getErrorId(htmlId);
    const tooltipId = getTooltipId(htmlId);
    const formikValue = getIn(formik.values, name);
    const formikLatError = getIn(formik.errors, `${name}[lat]`);
    const formikLatTouched = getIn(formik.touched, `${name}[lat]`);
    const inputHeight = theme?.input[variant].height || 40;
    const normalBorderColor = theme?.input[variant].borderColor || '#737373';
    const focusBorderColor =
      theme?.input[variant].focus.borderColor || theme?.colors.primary;
    const errorBorderColor =
      theme?.input[variant].error.borderColor || '#FD4329';
    const borderRadius = theme?.input[variant].borderRadius || 4;
    const borderColor =
      formikLatTouched && formikLatError ? errorBorderColor : normalBorderColor;

    const locationInputCss = css`
      height: ${inputHeight}px;
      border: 1px solid ${borderColor} !important;
      border-radius: ${borderRadius}px !important;

      &:focus {
        border: 1px solid ${focusBorderColor} !important;
      }
    `;

    return (
      <Box
        display="block"
        mb={6}
        opacity={disabled ? theme.input.disabled.opacity : 1}
        {...wrapperProps}
      >
        <Label
          id={htmlId}
          disabled={disabled}
          multiline={labelMultiline}
          required={required}
        >
          {label}
        </Label>

        <Field
          type="hidden"
          name={`${name}[lat]`}
          validate={(value) =>
            validateLocation({
              required,
              value,
            })
          }
          data-testid="formik-location"
        />
        <Tooltip initialPosition={POSITIONS.TOP} childrenElement={tooltip}>
          <InputLocationLegacy
            {...((formikLatError || ariaDescribedBy || tooltip) && {
              ariaDescribedBy: [
                formikLatError && errorId,
                ariaDescribedBy,
                tooltip && tooltipId,
              ]
                .filter(Boolean)
                .join(' '),
            })}
            componentRef={(ref) => (this.inputLocation = ref)}
            clearable
            defaultLocation={formikValue}
            disabled={disabled}
            icon={icon}
            id={htmlId}
            inputName="address"
            onlineEnabled={onlineEnabled}
            onlineLabel={onlineLabel}
            onSelectLocation={this.handleChange}
            onUpdateOnline={this.handleUpdateOnline}
            placeholder={placeholder}
            primaryInputName={fieldAddressName}
            inputHtml={{
              css: locationInputCss,
            }}
          />
        </Tooltip>
        {/* Make tooltip also available for ScreenReaders */}
        {tooltip && (
          <ScreenReadersContent id={tooltipId}>
            {typeof tooltip === 'function' ? tooltip() : tooltip}
          </ScreenReadersContent>
        )}
        {!hideError &&
          (formikLatTouched || formik.submitCount > 0) &&
          formikLatError && (
            <AdditionalInformation id={htmlId}>
              {formikLatError}
            </AdditionalInformation>
          )}
      </Box>
    );
  }
}

LocationLegacy.propTypes = {
  fieldAddressName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  ariaDescribedBy: PropTypes.string,
  hideError: PropTypes.bool,
  icon: PropTypes.func,
  id: PropTypes.string,
  label: PropTypes.string,
  labelMultiline: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onlineEnabled: PropTypes.bool,
  onlineLabel: PropTypes.string,
  onlineName: PropTypes.string,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
  }),
  wrapperProps: PropTypes.object,
};

LocationLegacy.defaultProps = {
  fieldAddressName: 'address',
  hideError: false,
  labelMultiline: true,
  validate: {},
  variant: 'normal',
  wrapperProps: {},
};

export default connect(({ componentRef, ...props }) => {
  const theme = useTheme();

  return <LocationLegacy ref={componentRef} theme={theme} {...props} />;
});
